import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMode } from '../context/FeaturesContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Activity, Dumbbell, Apple, LifeBuoy,
  CreditCard, Bluetooth, LogOut, User, Menu, X, ChevronRight,
  Calendar, Settings, Camera,
  Users, Trophy, Zap, Lock, LineChart
} from 'lucide-react';
import { useState } from 'react';
import NotificationInbox from '../components/layout/NotificationInbox';
import NotificationBell from '../components/layout/NotificationBell';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import UpgradeBanner from '../components/plan/UpgradeBanner';
import SimulationBanner from '../components/layout/SimulationBanner';
import ErrorBoundary from '../components/layout/ErrorBoundary';
import BrandLogo from '../components/layout/BrandLogo';
import NavItem from '../components/layout/NavItem';
import QuickActionsFab from '../components/layout/QuickActionsFab';
import { WorkoutControlsProvider } from '../context/WorkoutControlsContext';
import AppBackdrop from '../components/brand/AppBackdrop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MainLayout = () => {
  const { user, logout, isCoach, isAdmin } = useAuth();
  const { isFamily, isTeam, isPerformance } = useMode();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const { unreadCount, refresh } = useUnreadNotifications();

  // Base nav: items every user gets regardless of mode/plan.
  // Mode-specific items are appended below to keep the menu meaningful
  // for each user variant without showing irrelevant options.
  const baseNav = [
    { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/app/calendar', label: 'Calendario', icon: Calendar },
    { path: '/app/biometrics', label: 'Biometría', icon: Activity },
    { path: '/app/hub', label: 'Hub', icon: Dumbbell },
    { path: '/app/nutrition', label: 'Nutrición', icon: Apple },
    { path: '/app/progress/photos', label: 'Fotos', icon: Camera },
    { path: '/app/reports', label: 'Reportes', icon: LifeBuoy },
  ];

  // Mode-contextual items shown above billing
  const modeNav = [];
  if (isFamily) {
    modeNav.push({
      path: '/app/family',
      label: 'Mi Familia',
      icon: Users,
    });
  }
  if (isTeam) {
    modeNav.push({
      path: '/app/team',
      label: 'Mi Equipo',
      icon: Trophy,
    });
  }
  if (isPerformance) {
    modeNav.push({
      path: '/app/progress/performance',
      label: 'Rendimiento',
      icon: Zap,
    });
    modeNav.push({
      path: '/app/progress/insights',
      label: 'Análisis',
      icon: LineChart,
    });
  }

  const tailNav = [
    { path: '/app/devices', label: 'Dispositivos', icon: Bluetooth },
    { path: '/app/billing', label: 'Suscripción', icon: CreditCard },
  ];

  const navItems = [...baseNav, ...modeNav, ...tailNav];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <WorkoutControlsProvider>
    <div className="min-h-screen bg-zinc-950 flex relative">
      <AppBackdrop variant="user" />
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:w-64 flex-col border-r border-zinc-800 bg-zinc-950 relative z-20 tx">
        <div className="p-6 border-b border-zinc-800">
          <BrandLogo />
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            if (item.comingSoon) {
              return (
                <div
                  key={item.path}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-sm text-zinc-500 cursor-not-allowed"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title="Próximamente"
                >
                  <span className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    <span className="font-body text-sm">{item.label}</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-700 px-1.5 py-0.5 rounded-sm">
                    Pronto
                  </span>
                </div>
              );
            }
            return (
              <NavItem
                key={item.path}
                to={item.path}
                icon={item.icon}
                label={item.label}
                active={isActive(item.path)}
                testId={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              />
            );
          })}
        </nav>

        {/* Role Links */}
        {(isCoach() || isAdmin()) && (
          <div className="p-4 border-t border-zinc-800">
            {isCoach() && (
              <Link
                to="/coach/dashboard"
                className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-body mb-2"
              >
                <ChevronRight className="w-4 h-4" />
                Panel Coach
              </Link>
            )}
            {isAdmin() && (
              <Link
                to="/admin/dashboard"
                className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-body"
              >
                <ChevronRight className="w-4 h-4" />
                Panel Admin
              </Link>
            )}
          </div>
        )}

        {/* Settings & Notifications */}
        <div className="p-4 border-t border-zinc-800 space-y-1">
          <NotificationBell
            variant="sidebar"
            unreadCount={unreadCount}
            onClick={() => setInboxOpen(true)}
          />
          <Link
            to="/app/settings/notifications"
            className="flex items-center gap-3 px-4 py-3 rounded-sm text-zinc-400 hover:bg-zinc-900 hover:text-white transition-colors"
          >
            <Settings className="w-5 h-5" />
            <span className="font-body text-sm">Ajustes</span>
          </Link>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-zinc-800">
          <Link 
            to="/app/profile"
            className="flex items-center gap-3 mb-4 hover:bg-zinc-900 -mx-2 px-2 py-2 rounded transition-colors"
            data-testid="profile-link"
          >
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden">
              {user?.profile?.profile_photo_id ? (
                <img 
                  src={`${API}/profile/photo/${user.profile.profile_photo_id}`} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-5 h-5 text-zinc-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
            </div>
          </Link>
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-zinc-950 border-b border-zinc-800 mobile-header tx">
        <div className="flex items-center justify-between">
          <BrandLogo compact />
          <div className="flex items-center gap-3">
            <NotificationBell
              variant="mobile"
              unreadCount={unreadCount}
              onClick={() => setInboxOpen(true)}
            />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-zinc-400 hover:text-white"
              aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`
        lg:hidden fixed top-mobile-header left-0 bottom-0 w-64 z-30 bg-zinc-950 border-r border-zinc-800
        transform transition-transform duration-300 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <nav className="p-4 space-y-1 flex-1 min-h-0 overflow-y-auto">
          {navItems.map((item) => {
            if (item.comingSoon) {
              return (
                <div
                  key={item.path}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-sm text-zinc-500"
                >
                  <span className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    <span className="font-body text-sm">{item.label}</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-700 px-1.5 py-0.5 rounded-sm">
                    Pronto
                  </span>
                </div>
              );
            }
            return (
              <NavItem
                key={item.path}
                to={item.path}
                icon={item.icon}
                label={item.label}
                active={isActive(item.path)}
                onClick={() => setSidebarOpen(false)}
              />
            );
          })}
        </nav>
        {/* Al final del flujo y por encima de la barra inferior: nunca se encima con la lista */}
        <div className="shrink-0 p-4 border-t border-zinc-800 mb-[calc(5.5rem+env(safe-area-inset-bottom))]">
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-zinc-400"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 w-full lg:ml-0 mt-mobile-header relative z-10">
        <SimulationBanner />
        <UpgradeBanner />
        <div className="p-4 md:p-8 max-w-7xl mx-auto pb-mobile-nav min-w-0 w-full overflow-x-clip">
          <ErrorBoundary resetKey={location.pathname}>
            {/* key por ruta → re-dispara la transición de entrada al navegar */}
            <div key={location.pathname} className="page-enter">
              <Outlet />
            </div>
          </ErrorBoundary>
        </div>
      </main>

      {/* FAB de acciones rápidas (solo móvil) */}
      <QuickActionsFab />

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav">
        <div className="flex items-center justify-around">
          <Link
            to="/app/dashboard"
            className={`flex flex-col items-center gap-1 py-2 px-3 ${isActive('/app/dashboard') ? 'text-[hsl(var(--primary))]' : 'text-zinc-500'}`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span className="text-xs font-display font-semibold uppercase">Inicio</span>
          </Link>
          <Link
            to="/app/calendar"
            className={`flex flex-col items-center gap-1 py-2 px-3 ${isActive('/app/calendar') ? 'text-[hsl(var(--primary))]' : 'text-zinc-500'}`}
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs font-display font-semibold uppercase">Calendario</span>
          </Link>
          <span className="w-16 shrink-0" aria-hidden="true" />
          <Link
            to="/app/hub"
            className={`flex flex-col items-center gap-1 py-2 px-3 ${isActive('/app/hub') ? 'text-[hsl(var(--primary))]' : 'text-zinc-500'}`}
          >
            <Dumbbell className="w-5 h-5" />
            <span className="text-xs font-display font-semibold uppercase">Hub</span>
          </Link>
          <Link
            to="/app/biometrics"
            className={`flex flex-col items-center gap-1 py-2 px-3 ${isActive('/app/biometrics') ? 'text-[hsl(var(--primary))]' : 'text-zinc-500'}`}
          >
            <Activity className="w-5 h-5" />
            <span className="text-xs font-display font-semibold uppercase">Biometría</span>
          </Link>
        </div>
      </nav>

      {/* Notification Inbox */}
      <NotificationInbox
        isOpen={inboxOpen}
        onClose={() => {
          setInboxOpen(false);
          refresh();
        }}
      />
    </div>
    </WorkoutControlsProvider>
  );
};

export default MainLayout;
